import React, { useState, useEffect } from "react";
import "./3DemoNewCase.css";

const DemoNewCase = () => {
  // 기본 정보 상태 관리
  const [basicInfo, setBasicInfo] = useState({
    pt_name: "",
    pt_number: "",
    birth: "",
    age: "",
    sex: "",
    admissionDate: "",
    height: "",
    weight: "",
    waist_circumference: "",
    hip_circumference: "",
    ED_arrival_time: "",
    transfer: "",
    mainComplaint: "",
    pastHistory: [],
    medicationHistory: "",
    allergies: [],
    isFastTrack: false,
    assignedDoctor: "",
    priority: "normal",
    notes: "",
  });

  // 데이터 컬럼 설정 상태 관리
  const [columnSettings, setColumnSettings] = useState({
    hbo2_ch1: "hbo2_ch",
    hbr_ch1: "hbr_ch1",
    hbo2_ch2: "hbo2_ch2",
    hbr_ch2: "hbr_ch2",
    hbo2_ch3: "hbo2_ch3",
    hbr_ch3: "hbr_ch3",
    samplingRate: 200,
  });

  // HUP 디바이스 설정 상태 관리
  const [deviceSettings, setDeviceSettings] = useState({
    deviceAngle: "",
    deviceHeight: "",
    devicePosition: "",
    devicePressure: "",
    measurementDuration: "",
    calibrationStatus: "uncalibrated",
    lastCalibrationDate: "",
    deviceSerialNumber: "",
  });

  // CBF 데이터 상태
  const [cbfData, setCbfData] = useState(null);

  // 샘플 CBF 데이터 생성 함수
  const generateSampleData = () => {
    const samplingRate = 200; // Hz
    const duration = 10; // 10초
    const samples = samplingRate * duration;
    const timeStep = 1 / samplingRate;

    // CBF 데이터 (Cerebral Blood Flow)
    const cbfData = Array.from({ length: samples }, (_, i) => {
      const t = i * timeStep;
      const baseFrequency = 1.2; // Hz
      return {
        time: t,
        value:
          60 + // 기준값
          10 * Math.sin(2 * Math.PI * baseFrequency * t) + // 주기적 변동
          5 * Math.sin(2 * Math.PI * 0.1 * t) + // 저주파 변동
          2 * (Math.random() - 0.5), // 노이즈
      };
    });

    return {
      cbf: cbfData,
    };
  };

  // 컴포넌트 마운트 시 샘플 데이터 생성
  useEffect(() => {
    setCbfData(generateSampleData());
  }, []);

  // SVG 경로 생성 함수
  const generatePath = (data, width, height) => {
    if (!data || data.length === 0) return "";

    const minX = 0;
    const maxX = 10; // 10초
    const minY = 40;
    const maxY = 80;

    const scaleX = width / (maxX - minX);
    const scaleY = height / (maxY - minY);

    return data
      .map((point, i) => {
        const x = (point.time - minX) * scaleX;
        const y = height - (point.value - minY) * scaleY;
        return `${i === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");
  };

  const CBFGraph = ({ data, color, title, type = "HbO2" }) => {
    const containerWidth = 1200;
    const containerHeight = 300;
    const graphWidth = containerWidth - 120; // 여백 증가
    const graphHeight = containerHeight - 80;
    const padding = {
      top: 30,
      right: 30,
      bottom: 40,
      left: 90, // Y축 라벨 공간 확보
    };
    const viewBox = `0 0 ${containerWidth} ${containerHeight}`;

    // CBF 타입별 Y축 범위 설정
    const getYAxisRange = (type) => {
      switch (type) {
        case "HbO2":
          return { min: -10, max: 50, unit: "μM·cm" };
        case "HbR":
          return { min: -20, max: 20, unit: "μM·cm" };
        case "HbT":
          return { min: -10, max: 60, unit: "μM·cm" };
        case "StO2":
          return { min: 60, max: 85, unit: "%" };
        default:
          return { min: -20, max: 50, unit: "μM·cm" };
      }
    };

    const yRange = getYAxisRange(type);
    const ySpan = yRange.max - yRange.min;

    // CBF 특성을 반영한 시뮬레이션 데이터 생성
    const generateCBFData = (baseData, type) => {
      const stimulusStart = 2; // 2초에 자극 시작
      const stimulusEnd = 7; // 7초에 자극 종료

      return baseData.map((point, index) => {
        const time = (index / baseData.length) * 30; // 30초 시간축
        let value = 0;

        if (time >= stimulusStart && time <= stimulusEnd + 15) {
          const relativeTime = time - stimulusStart;

          switch (type) {
            case "HbO2":
              // 2-4초 지연, 6-10초 피크, 완만한 상승
              if (relativeTime >= 2) {
                const peakTime = 8;
                const amplitude = 35;
                value =
                  amplitude *
                  Math.exp(-Math.pow(relativeTime - peakTime, 2) / 20) *
                  (1 - Math.exp(-relativeTime / 3));
              }
              break;

            case "HbR":
              // 초기 하강 후 약간 상승
              if (relativeTime >= 1) {
                const initialDrop =
                  -12 * Math.exp(-Math.pow(relativeTime - 4, 2) / 15);
                const laterRise =
                  8 * Math.exp(-Math.pow(relativeTime - 10, 2) / 25);
                value = initialDrop + (relativeTime > 6 ? laterRise : 0);
              }
              break;

            case "HbT":
              // HbO2 + HbR의 합
              const hbo2 =
                relativeTime >= 2
                  ? 35 *
                    Math.exp(-Math.pow(relativeTime - 8, 2) / 20) *
                    (1 - Math.exp(-relativeTime / 3))
                  : 0;
              const hbr =
                relativeTime >= 1
                  ? -12 * Math.exp(-Math.pow(relativeTime - 4, 2) / 15) +
                    (relativeTime > 6
                      ? 8 * Math.exp(-Math.pow(relativeTime - 10, 2) / 25)
                      : 0)
                  : 0;
              value = hbo2 + hbr;
              break;

            case "StO2":
              // 산소 포화도 증가
              if (relativeTime >= 2) {
                value =
                  70 +
                  12 *
                    Math.exp(-Math.pow(relativeTime - 8, 2) / 20) *
                    (1 - Math.exp(-relativeTime / 3));
              } else {
                value = 70;
              }
              break;
          }
        }

        // 기준선 + 노이즈 추가
        const baseline = type === "StO2" ? 70 : 0;
        const noise = (Math.random() - 0.5) * 2;
        return baseline + value + noise;
      });
    };

    const cbfData = generateCBFData(data, type);

    // 경로 생성 함수 개선
    const generateCBFPath = (data, width, height) => {
      if (!data || data.length === 0) return "";

      const points = data.map((value, index) => {
        const x = (index / (data.length - 1)) * width;
        const normalizedY = (value - yRange.min) / ySpan;
        const y = height * (1 - normalizedY);
        return { x, y };
      });

      // 부드러운 곡선을 위한 베지어 곡선 사용
      let path = `M ${points[0].x} ${points[0].y}`;

      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const next = points[i + 1];

        if (next) {
          const cp1x = prev.x + (curr.x - prev.x) * 0.5;
          const cp1y = prev.y;
          const cp2x = curr.x - (next.x - curr.x) * 0.5;
          const cp2y = curr.y;

          path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`;
        } else {
          path += ` L ${curr.x} ${curr.y}`;
        }
      }

      return path;
    };

    // Y축 눈금 생성
    const generateYTicks = () => {
      const tickCount = 6;
      const step = ySpan / (tickCount - 1);
      return Array.from(
        { length: tickCount },
        (_, i) => Math.round((yRange.min + step * i) * 10) / 10
      );
    };

    return (
      <div className="chart-wrapper">
        <h3>
          {title} ({yRange.unit})
        </h3>
        <div className="chart-container">
          <svg
            width="100%"
            height="100%"
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            className="cbf-graph"
          >
            <g transform={`translate(${padding.left}, ${padding.top})`}>
              {/* 격자 배경 */}
              <defs>
                <pattern
                  id="grid"
                  width="40"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 40 0 L 0 0 0 20"
                    fill="none"
                    stroke="#f0f0f0"
                    strokeWidth="0.5"
                  />
                </pattern>
              </defs>
              <rect width={graphWidth} height={graphHeight} fill="url(#grid)" />

              {/* 기준선 (0선) */}
              {yRange.min < 0 && yRange.max > 0 && (
                <line
                  x1="0"
                  y1={graphHeight * (yRange.max / ySpan)}
                  x2={graphWidth}
                  y2={graphHeight * (yRange.max / ySpan)}
                  stroke="#999"
                  strokeWidth="1"
                  strokeDasharray="5,5"
                />
              )}

              {/* X축 */}
              <line
                x1="0"
                y1={graphHeight}
                x2={graphWidth}
                y2={graphHeight}
                stroke="#333"
                strokeWidth="2"
              />

              {/* Y축 */}
              <line
                x1="0"
                y1="0"
                x2="0"
                y2={graphHeight}
                stroke="#333"
                strokeWidth="2"
              />

              {/* X축 눈금 (0-30초) */}
              {[0, 5, 10, 15, 20, 25, 30].map((tick) => (
                <g
                  key={tick}
                  transform={`translate(${
                    graphWidth * (tick / 30)
                  }, ${graphHeight})`}
                >
                  <line y2="8" stroke="#333" strokeWidth="1" />
                  <text y="25" textAnchor="middle" fill="#333" fontSize="12">
                    {tick}s
                  </text>
                </g>
              ))}

              {/* Y축 눈금 */}
              {generateYTicks().map((tick) => (
                <g
                  key={tick}
                  transform={`translate(0, ${
                    graphHeight * (1 - (tick - yRange.min) / ySpan)
                  })`}
                >
                  <line x2="-8" stroke="#333" strokeWidth="1" />
                  <text
                    x="-15"
                    y="5"
                    textAnchor="end"
                    fill="#333"
                    fontSize="12"
                  >
                    {tick}
                  </text>
                </g>
              ))}

              {/* Y축 라벨 */}
              <text
                x="-60"
                y={graphHeight / 2}
                textAnchor="middle"
                fill="#333"
                fontSize="14"
                transform={`rotate(-90, -60, ${graphHeight / 2})`}
              >
                {type} ({yRange.unit})
              </text>

              {/* CBF 데이터 경로 */}
              <path
                d={generateCBFPath(cbfData, graphWidth, graphHeight)}
                fill="none"
                stroke={color}
                strokeWidth="2.5"
                opacity="0.8"
              />

              {/* 데이터 포인트 (선택적) */}
              {cbfData
                .filter((_, i) => i % 10 === 0)
                .map((value, index) => {
                  const actualIndex = index * 10;
                  const x = (actualIndex / (cbfData.length - 1)) * graphWidth;
                  const normalizedY = (value - yRange.min) / ySpan;
                  const y = graphHeight * (1 - normalizedY);

                  return (
                    <circle
                      key={actualIndex}
                      cx={x}
                      cy={y}
                      r="3"
                      fill={color}
                      opacity="0.7"
                    />
                  );
                })}
            </g>
          </svg>
        </div>
      </div>
    );
  };

  // 사용 예시
  const App = () => {
    const dummyData = Array.from({ length: 300 }, (_, i) => i);

    return (
      <div>
        <CBFGraph
          data={dummyData}
          color="#e74c3c"
          title="산소화 헤모글로빈"
          type="HbO2"
        />
        <CBFGraph
          data={dummyData}
          color="#3498db"
          title="탈산소화 헤모글로빈"
          type="HbR"
        />
        <CBFGraph
          data={dummyData}
          color="#2ecc71"
          title="총 헤모글로빈"
          type="HbT"
        />
        <CBFGraph
          data={dummyData}
          color="#9b59b6"
          title="조직 산소 포화도"
          type="StO2"
        />
      </div>
    );
  };

  return (
    <div className="vip-11-container">
      <div className="vip-content">
        <div className="vip-form-container">
          {/* 기본 정보 섹션 - 2DemoNewCase.js와 동일 */}
          <div className="vip-form-section">
            <h2>기본 정보</h2>
            <div className="basic-info-fieldset">
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">환자이름</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="pt_name"
                      name="pt_name"
                      value={basicInfo.pt_name}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, pt_name: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">환자번호</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="pt_number"
                      name="pt_number"
                      value={basicInfo.pt_number}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          pt_number: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">생년월일</label>
                  <div className="input_2col">
                    <input
                      type="date"
                      id="birth"
                      name="birth"
                      value={basicInfo.birth}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, birth: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">나이</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="age"
                      name="age"
                      value={basicInfo.age}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, age: e.target.value })
                      }
                      readOnly={!!basicInfo.birth}
                    />
                    <span className="unit-text">세</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">성별</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        name="sex"
                        value="male"
                        checked={basicInfo.sex === "male"}
                        onChange={(e) =>
                          setBasicInfo({ ...basicInfo, sex: e.target.value })
                        }
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">남성</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        name="sex"
                        value="female"
                        checked={basicInfo.sex === "female"}
                        onChange={(e) =>
                          setBasicInfo({ ...basicInfo, sex: e.target.value })
                        }
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">여성</div>
                    </label>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">입원일</label>
                  <div className="input_2col">
                    <input
                      type="date"
                      id="admissionDate"
                      name="admissionDate"
                      value={basicInfo.admissionDate}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          admissionDate: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">키</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="height"
                      name="height"
                      value={basicInfo.height}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, height: e.target.value })
                      }
                    />
                    <span className="unit-text">cm</span>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">몸무게</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="weight"
                      name="weight"
                      value={basicInfo.weight}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, weight: e.target.value })
                      }
                    />
                    <span className="unit-text">kg</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">허리 둘레</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="waist_circumference"
                      name="waist_circumference"
                      value={basicInfo.waist_circumference}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          waist_circumference: e.target.value,
                        })
                      }
                    />
                    <span className="unit-text">cm</span>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">엉덩이 둘레</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="hip_circumference"
                      name="hip_circumference"
                      value={basicInfo.hip_circumference}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          hip_circumference: e.target.value,
                        })
                      }
                    />
                    <span className="unit-text">cm</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">응급실 도착시각</label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="ED_arrival_time"
                      name="ED_arrival_time"
                      max="9999-12-31T23:59"
                      value={basicInfo.ED_arrival_time}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          ED_arrival_time: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">타 병원에서 전원 옴</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        name="transfer"
                        value="yes"
                        checked={basicInfo.transfer === "yes"}
                        onChange={(e) =>
                          setBasicInfo({
                            ...basicInfo,
                            transfer: e.target.value,
                          })
                        }
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        name="transfer"
                        value="no"
                        checked={basicInfo.transfer === "no"}
                        onChange={(e) =>
                          setBasicInfo({
                            ...basicInfo,
                            transfer: e.target.value,
                          })
                        }
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">주호소</label>
                  <div className="input_1col">
                    <input
                      type="text"
                      id="mainComplaint"
                      name="mainComplaint"
                      value={basicInfo.mainComplaint}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          mainComplaint: e.target.value,
                        })
                      }
                      placeholder="주요 증상이나 호소 사항을 입력하세요"
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">과거력</label>
                  <div className="input_1col">
                    <div className="checkbox-group">
                      {["고혈압", "당뇨", "심장질환", "뇌졸중", "암"].map(
                        (option) => (
                          <label key={option} className="checkbox-label">
                            <input
                              type="checkbox"
                              value={option}
                              checked={basicInfo.pastHistory.includes(option)}
                              onChange={(e) => {
                                const value = e.target.value;
                                const isChecked = e.target.checked;
                                setBasicInfo((prev) => ({
                                  ...prev,
                                  pastHistory: isChecked
                                    ? [...prev.pastHistory, value]
                                    : prev.pastHistory.filter(
                                        (item) => item !== value
                                      ),
                                }));
                              }}
                            />
                            {option}
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">복용 중인 약물</label>
                  <div className="input_1col">
                    <textarea
                      id="medicationHistory"
                      name="medicationHistory"
                      value={basicInfo.medicationHistory}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          medicationHistory: e.target.value,
                        })
                      }
                      placeholder="현재 복용 중인 약물을 입력하세요"
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">알레르기</label>
                  <div className="input_1col">
                    <div className="checkbox-group">
                      {["페니실린", "설파제", "조영제", "없음"].map(
                        (option) => (
                          <label key={option} className="checkbox-label">
                            <input
                              type="checkbox"
                              value={option}
                              checked={basicInfo.allergies.includes(option)}
                              onChange={(e) => {
                                const value = e.target.value;
                                const isChecked = e.target.checked;
                                setBasicInfo((prev) => ({
                                  ...prev,
                                  allergies: isChecked
                                    ? [...prev.allergies, value]
                                    : prev.allergies.filter(
                                        (item) => item !== value
                                      ),
                                }));
                              }}
                            />
                            {option}
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">메모</label>
                  <div className="input_1col">
                    <textarea
                      id="notes"
                      name="notes"
                      value={basicInfo.notes}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, notes: e.target.value })
                      }
                      placeholder="추가 메모사항을 입력하세요"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* HUP 디바이스 설정 섹션 */}
          <div className="vip-form-section">
            <h2>HUP Device 데이터</h2>
            <div className="device-settings-container">
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">머리 거상 각도</label>
                  <div className="input_2col">
                    <input
                      type="number"
                      value={deviceSettings.deviceAngle}
                      onChange={(e) =>
                        setDeviceSettings({
                          ...deviceSettings,
                          deviceAngle: e.target.value,
                        })
                      }
                      placeholder="예: 30"
                    />
                    <span className="unit-text">°</span>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">
                    최저-최대 위치 상승 시간
                  </label>
                  <div className="input_2col">
                    <input
                      type="number"
                      value={deviceSettings.deviceHeight}
                      onChange={(e) =>
                        setDeviceSettings({
                          ...deviceSettings,
                          deviceHeight: e.target.value,
                        })
                      }
                      placeholder="예: 100"
                    />
                    <span className="unit-text">초</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">상승 속도</label>
                  <div className="input_2col">
                    <input
                      type="number"
                      value={deviceSettings.measurementDuration}
                      onChange={(e) =>
                        setDeviceSettings({
                          ...deviceSettings,
                          measurementDuration: e.target.value,
                        })
                      }
                      placeholder="예: 10"
                    />
                    <span className="unit-text">cm/min</span>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">압력 센서 값</label>
                  <div className="input_2col">
                    <input
                      type="number"
                      value={deviceSettings.measurementDuration}
                      onChange={(e) =>
                        setDeviceSettings({
                          ...deviceSettings,
                          measurementDuration: e.target.value,
                        })
                      }
                      placeholder="예: 10"
                    />
                    <span className="unit-text"></span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">최근 보정일</label>
                  <div className="input_2col">
                    <input
                      type="date"
                      value={deviceSettings.lastCalibrationDate}
                      onChange={(e) =>
                        setDeviceSettings({
                          ...deviceSettings,
                          lastCalibrationDate: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">시리얼 번호</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      value={deviceSettings.deviceSerialNumber}
                      onChange={(e) =>
                        setDeviceSettings({
                          ...deviceSettings,
                          deviceSerialNumber: e.target.value,
                        })
                      }
                      placeholder="예: HUP-2024-001"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 시계열 데이터 업로드 섹션 */}
          <div className="vip-form-section">
            <h2>시계열 데이터 업로드</h2>
            <div className="file-upload-container">
              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">CSV 파일</label>
                  <div className="input_1col">
                    <div className="file-upload-box">
                      <div className="file-upload-status">
                        <div className="file-info">
                          <span className="file-name">sample_data.csv</span>
                          <span className="file-size">(2.5MB)</span>
                        </div>
                        <button type="button" className="file-change-button">
                          파일 변경
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="section-subtitle">데이터 컬럼 설정</div>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    Ch1, HBO2 의 Column 명
                  </label>
                  <div className="input_2col">
                    <input
                      type="text"
                      value={columnSettings.hbo2_ch1}
                      onChange={(e) =>
                        setColumnSettings({
                          ...columnSettings,
                          hbo2_ch1: e.target.value,
                        })
                      }
                      placeholder="예: hbo2_ch1"
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">
                    Ch1, HBR 의 Column 명
                  </label>
                  <div className="input_2col">
                    <input
                      type="text"
                      value={columnSettings.hbr_ch1}
                      onChange={(e) =>
                        setColumnSettings({
                          ...columnSettings,
                          hbr_ch1: e.target.value,
                        })
                      }
                      placeholder="예: hbr_ch1"
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    Ch2, HBO2 의 Column 명
                  </label>
                  <div className="input_2col">
                    <input
                      type="text"
                      value={columnSettings.hbo2_ch2}
                      onChange={(e) =>
                        setColumnSettings({
                          ...columnSettings,
                          hbo2_ch2: e.target.value,
                        })
                      }
                      placeholder="예: hbo2_ch2"
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">
                    Ch2, HBR 의 Column 명
                  </label>
                  <div className="input_2col">
                    <input
                      type="text"
                      value={columnSettings.hbr_ch2}
                      onChange={(e) =>
                        setColumnSettings({
                          ...columnSettings,
                          hbr_ch2: e.target.value,
                        })
                      }
                      placeholder="예: hbr_ch2"
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">샘플링 주파수 (Hz)</label>
                  <div className="input_2col">
                    <input
                      type="number"
                      value={columnSettings.samplingRate}
                      onChange={(e) =>
                        setColumnSettings({
                          ...columnSettings,
                          samplingRate: parseInt(e.target.value) || 0,
                        })
                      }
                      placeholder="예: 200"
                    />
                  </div>
                </div>
                <div className="block-2column"></div>
              </div>
            </div>
          </div>

          {/* CBF 데이터 시각화 섹션 */}
          <div className="data-visualization-section">
            <h2>CBF 데이터 분석</h2>
            <div className="charts-container">
              {cbfData && (
                <CBFGraph
                  data={cbfData.cbf}
                  color="#E91E63"
                  title="Cerebral Blood Flow (CBF)"
                />
              )}
            </div>
          </div>

          {/* 저장 버튼 */}
          <div className="save-button-container">
            <button className="vip-submit-button large">저장하기</button>
          </div>

          {/* 통계 대시보드 섹션 */}
          <div className="vip-form-section statistics-dashboard">
            <h2>디바이스 센서 통계 대시보드</h2>

            <div className="statistics-summary">
              <div className="stat-card">
                <h3>총 센서 데이터</h3>
                <div className="stat-value">8,742</div>
                <div className="stat-trend positive">
                  <span>+2.8%</span> 이전 세션 대비
                </div>
              </div>

              <div className="stat-card">
                <h3>평균 디바이스 각도</h3>
                <div className="stat-value">15.3°</div>
                <div className="stat-trend neutral">
                  <span>±2.1°</span> 최적 범위
                </div>
              </div>

              <div className="stat-card">
                <h3>압력 센서 정확도</h3>
                <div className="stat-value">97.8%</div>
                <div className="stat-trend positive">
                  <span>+1.5%</span> 이전 측정 대비
                </div>
              </div>

              <div className="stat-card">
                <h3>자이로 센서 안정성</h3>
                <div className="stat-value">99.1%</div>
                <div className="stat-trend positive">
                  <span>+0.6%</span> 이전 측정 대비
                </div>
              </div>
            </div>

            <div className="statistics-grid">
              <div className="chart-container">
                <div className="chart-header">
                  <h3>디바이스 각도 분포 (Roll/Pitch/Yaw)</h3>
                </div>
                <div className="bar-chart">
                  <div className="chart-bars">
                    {[
                      { value: 0.85, label: "Roll", color: "#3b82f6" },
                      { value: 0.92, label: "Pitch", color: "#10b981" },
                      { value: 0.78, label: "Yaw", color: "#f59e0b" },
                      { value: 0.88, label: "X축", color: "#8b5cf6" },
                      { value: 0.91, label: "Y축", color: "#ef4444" },
                      { value: 0.82, label: "Z축", color: "#06b6d4" },
                    ].map((item, index) => (
                      <div key={index} className="chart-bar-wrapper">
                        <div
                          className="chart-bar"
                          style={{
                            height: `${item.value * 180}px`,
                            backgroundColor: item.color,
                          }}
                        >
                          <span className="bar-value">
                            {Math.round(item.value * 100)}%
                          </span>
                        </div>
                        <span className="bar-label">{item.label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="chart-description">
                    <small>각 축별 안정성 지수 (0-100%)</small>
                  </div>
                </div>
              </div>

              <div className="chart-container">
                <div className="chart-header">
                  <h3>압력 센서 시간별 변화</h3>
                </div>
                <div className="line-chart">
                  <svg className="line-svg" viewBox="0 0 800 240">
                    {/* 격자 */}
                    {[...Array(6)].map((_, i) => (
                      <line
                        key={`grid-h-${i}`}
                        x1="50"
                        y1={30 + i * 36}
                        x2="750"
                        y2={30 + i * 36}
                        stroke="#f1f5f9"
                        strokeWidth="1"
                      />
                    ))}
                    {[...Array(8)].map((_, i) => (
                      <line
                        key={`grid-v-${i}`}
                        x1={50 + i * 100}
                        y1="30"
                        x2={50 + i * 100}
                        y2="210"
                        stroke="#f1f5f9"
                        strokeWidth="1"
                      />
                    ))}

                    {/* 축 */}
                    <line
                      x1="50"
                      y1="30"
                      x2="50"
                      y2="210"
                      stroke="#cbd5e1"
                      strokeWidth="2"
                    />
                    <line
                      x1="50"
                      y1="210"
                      x2="750"
                      y2="210"
                      stroke="#cbd5e1"
                      strokeWidth="2"
                    />

                    {/* 압력 센서 1 */}
                    <path
                      d="M 50 180 Q 150 120, 250 140 T 450 100 T 650 120 L 750 90"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="3"
                    />

                    {/* 압력 센서 2 */}
                    <path
                      d="M 50 160 Q 150 100, 250 120 T 450 80 T 650 100 L 750 70"
                      fill="none"
                      stroke="#ef4444"
                      strokeWidth="3"
                    />

                    {/* 데이터 포인트 */}
                    {[
                      { x: 150, y: 120, sensor: 1 },
                      { x: 250, y: 140, sensor: 1 },
                      { x: 450, y: 100, sensor: 1 },
                      { x: 650, y: 120, sensor: 1 },
                      { x: 150, y: 100, sensor: 2 },
                      { x: 250, y: 120, sensor: 2 },
                      { x: 450, y: 80, sensor: 2 },
                      { x: 650, y: 100, sensor: 2 },
                    ].map((point, index) => (
                      <circle
                        key={index}
                        cx={point.x}
                        cy={point.y}
                        r="4"
                        fill={point.sensor === 1 ? "#3b82f6" : "#ef4444"}
                        stroke="white"
                        strokeWidth="2"
                      />
                    ))}

                    {/* Y축 라벨 */}
                    <text x="25" y="35" fontSize="10" fill="#64748b">
                      100kPa
                    </text>
                    <text x="25" y="75" fontSize="10" fill="#64748b">
                      80kPa
                    </text>
                    <text x="25" y="115" fontSize="10" fill="#64748b">
                      60kPa
                    </text>
                    <text x="25" y="155" fontSize="10" fill="#64748b">
                      40kPa
                    </text>
                    <text x="25" y="195" fontSize="10" fill="#64748b">
                      20kPa
                    </text>

                    {/* X축 라벨 */}
                    <text x="50" y="230" fontSize="10" fill="#64748b">
                      0s
                    </text>
                    <text x="250" y="230" fontSize="10" fill="#64748b">
                      10s
                    </text>
                    <text x="450" y="230" fontSize="10" fill="#64748b">
                      20s
                    </text>
                    <text x="650" y="230" fontSize="10" fill="#64748b">
                      30s
                    </text>

                    {/* 범례 */}
                    <circle cx="600" cy="20" r="4" fill="#3b82f6" />
                    <text x="610" y="23" fontSize="12" fill="#64748b">
                      센서 1
                    </text>
                    <circle cx="660" cy="20" r="4" fill="#ef4444" />
                    <text x="670" y="23" fontSize="12" fill="#64748b">
                      센서 2
                    </text>
                  </svg>
                </div>
              </div>

              <div className="chart-container">
                <div className="chart-header">
                  <h3>3D 각도 분포 매핑</h3>
                </div>
                <div className="scatter-plot">
                  <svg className="scatter-svg" viewBox="0 0 800 240">
                    {/* 격자 */}
                    {[...Array(6)].map((_, i) => (
                      <line
                        key={`grid-h-${i}`}
                        x1="60"
                        y1={30 + i * 36}
                        x2="740"
                        y2={30 + i * 36}
                        stroke="#f1f5f9"
                        strokeWidth="1"
                      />
                    ))}
                    {[...Array(8)].map((_, i) => (
                      <line
                        key={`grid-v-${i}`}
                        x1={60 + i * 95}
                        y1="30"
                        x2={60 + i * 95}
                        y2="210"
                        stroke="#f1f5f9"
                        strokeWidth="1"
                      />
                    ))}

                    {/* 축 */}
                    <line
                      x1="60"
                      y1="30"
                      x2="60"
                      y2="210"
                      stroke="#cbd5e1"
                      strokeWidth="2"
                    />
                    <line
                      x1="60"
                      y1="210"
                      x2="740"
                      y2="210"
                      stroke="#cbd5e1"
                      strokeWidth="2"
                    />

                    {/* 각도 분포 데이터 포인트 */}
                    {Array.from({ length: 25 }, (_, i) => {
                      const angle = (i / 24) * 2 * Math.PI;
                      const radius = 30 + Math.random() * 60;
                      const centerX = 400;
                      const centerY = 120;
                      return {
                        x: centerX + Math.cos(angle) * radius,
                        y: centerY + Math.sin(angle) * radius,
                        type:
                          i % 3 === 0 ? "roll" : i % 3 === 1 ? "pitch" : "yaw",
                        intensity: Math.random(),
                      };
                    }).map((point, index) => (
                      <circle
                        key={index}
                        cx={point.x}
                        cy={point.y}
                        r={3 + point.intensity * 4}
                        fill={
                          point.type === "roll"
                            ? "#3b82f6"
                            : point.type === "pitch"
                            ? "#10b981"
                            : "#f59e0b"
                        }
                        opacity={0.6 + point.intensity * 0.4}
                        stroke="white"
                        strokeWidth="1"
                      />
                    ))}

                    {/* 중심점 */}
                    <circle
                      cx="400"
                      cy="120"
                      r="3"
                      fill="#64748b"
                      stroke="white"
                      strokeWidth="2"
                    />
                    <text
                      x="400"
                      y="135"
                      textAnchor="middle"
                      fontSize="10"
                      fill="#64748b"
                    >
                      기준점
                    </text>

                    {/* 축 라벨 */}
                    <text x="30" y="35" fontSize="10" fill="#64748b">
                      +30°
                    </text>
                    <text x="30" y="120" fontSize="10" fill="#64748b">
                      0°
                    </text>
                    <text x="30" y="205" fontSize="10" fill="#64748b">
                      -30°
                    </text>

                    <text x="60" y="230" fontSize="10" fill="#64748b">
                      -45°
                    </text>
                    <text x="400" y="230" fontSize="10" fill="#64748b">
                      0°
                    </text>
                    <text x="740" y="230" fontSize="10" fill="#64748b">
                      +45°
                    </text>

                    {/* 범례 */}
                    <circle cx="620" cy="20" r="4" fill="#3b82f6" />
                    <text x="630" y="23" fontSize="11" fill="#64748b">
                      Roll
                    </text>
                    <circle cx="660" cy="20" r="4" fill="#10b981" />
                    <text x="670" y="23" fontSize="11" fill="#64748b">
                      Pitch
                    </text>
                    <circle cx="710" cy="20" r="4" fill="#f59e0b" />
                    <text x="720" y="23" fontSize="11" fill="#64748b">
                      Yaw
                    </text>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoNewCase;
